// "Resumen de matrícula": los datos que antes la asesora enviaba por WhatsApp
// (mismo orden del listado), listos para copiar uno por uno o todos juntos.
import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon, CopyIcon } from '@chakra-ui/icons';
import dayjs from 'dayjs';

import xhr from 'xhr';

const clean = (value) => (value === null || value === undefined ? '' : String(value).trim());
const fullName = (user) => clean(`${clean(user?.last_name)} ${clean(user?.first_name)}`);

const buildSections = (student, parent) => {
  const user = student?.user || {};
  const phoneCode = user.phone_code || user.country?.phone_code;
  const city = [clean(student?.city), clean(student?.state)].filter(Boolean).join(', ');

  return [
    {
      title: 'Estudiante',
      rows: [
        { label: 'Nombre completo (apellidos y nombres)', value: fullName(user) },
        { label: 'Tipo de documento', value: clean(student?.identity_type?.name) },
        { label: 'Número de documento', value: clean(student?.identity).replace(/\D/g, '') || clean(student?.identity) },
        { label: 'Ciudad de expedición', value: clean(student?.expedition_place) },
        { label: 'Fecha de nacimiento', value: student?.birthdate ? dayjs(student.birthdate).format('DD/MM/YYYY') : '' },
        { label: 'Número celular', value: clean(user.cellphone), hint: phoneCode ? `+${phoneCode}` : '' },
        { label: 'Ciudad de residencia', value: city },
        { label: 'País de residencia', value: clean(student?.country?.name || user.country?.name) },
        { label: 'Correo electrónico', value: clean(user.email) },
        { label: 'Último grado aprobado', value: clean(student?.last_approved_grade) },
        { label: 'Condición médica', value: clean(student?.desease) },
      ],
    },
    {
      title: parent?.parentesco ? `Acudiente (${parent.parentesco})` : 'Acudiente',
      empty: !parent && 'No tiene acudiente registrado.',
      rows: parent
        ? [
            { label: 'Nombre completo', value: clean(parent.full_name) },
            { label: 'Tipo de documento', value: clean(parent.identity_type?.name) },
            { label: 'Número de documento', value: clean(parent.identity).replace(/\D/g, '') || clean(parent.identity) },
            { label: 'Ciudad de expedición', value: clean(parent.expedition_place) },
            { label: 'Número celular', value: clean(parent.home_phone || parent.user?.cellphone) },
            { label: 'Correo electrónico', value: clean(parent.email || parent.user?.email) },
          ]
        : [],
    },
  ];
};

const toPlainText = (sections) =>
  sections
    .map((section) =>
      [section.title.toUpperCase(), ...section.rows.map((row) => `${row.label}: ${row.value || 'Sin dato'}`)].join('\n')
    )
    .join('\n\n');

function CopyRow({ label, value, hint }) {
  const toast = useToast();
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error(error);
      toast({ title: 'No se pudo copiar. Selecciona el texto y cópialo a mano.', status: 'warning', duration: 3000 });
    }
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={2}
      px={3}
      borderBottom="1px solid"
      borderColor="gray.100"
      _last={{ borderBottom: 0 }}
    >
      <Box minW={0}>
        <Text fontSize="xs" color="gray.500">
          {label}
        </Text>
        {value ? (
          <Text fontSize="sm" fontWeight="500" color="gray.800" wordBreak="break-word" userSelect="all">
            {hint && (
              <Text as="span" color="gray.400" mr={1} userSelect="none">
                {hint}
              </Text>
            )}
            {value}
          </Text>
        ) : (
          <Badge colorScheme="yellow" fontWeight="500" textTransform="none">
            Sin dato
          </Badge>
        )}
      </Box>
      {value && (
        <Tooltip label={copied ? 'Copiado' : 'Copiar'} hasArrow>
          <IconButton
            aria-label={`Copiar ${label}`}
            icon={copied ? <CheckIcon color="green.500" /> : <CopyIcon />}
            size="sm"
            variant="ghost"
            colorScheme="purple"
            onClick={copy}
          />
        </Tooltip>
      )}
    </Flex>
  );
}

export default function StudentSummaryModal({ studentId, isOpen, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);
  const [student, setStudent] = React.useState(null);
  const [parent, setParent] = React.useState(null);
  const [lastReview, setLastReview] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen || !studentId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [resStudent, resParents, resHistory] = await Promise.all([
          xhr(
            `/students/${studentId}?populate[0]=user&populate[1]=identity_type&populate[2]=country&populate[3]=user.country`
          ),
          xhr(
            `/parents?filters[students][id][$eq]=${studentId}&populate[0]=identity_type&populate[1]=user&sort[0]=createdAt:desc&pagination[pageSize]=10`
          ),
          xhr(
            `/student-histories?filters[student][id][$eq]=${studentId}&filters[detail][$startsWith]=Datos corregidos&populate=user&sort[0]=createdAt:desc&pagination[pageSize]=1`
          ),
        ]);
        if (cancelled) return;
        setStudent(resStudent.data.data);
        setParent((resParents.data.data || [])[0] || null);
        setLastReview((resHistory.data.data || [])[0] || null);
      } catch (error) {
        console.error(error);
        toast({ title: 'No se pudo cargar el resumen.', status: 'error', duration: 3000 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, studentId, toast]);

  const sections = student ? buildSections(student, parent) : [];

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(toPlainText(sections));
      toast({ title: 'Resumen copiado.', status: 'success', duration: 2000 });
    } catch (error) {
      console.error(error);
      toast({ title: 'No se pudo copiar el resumen.', status: 'warning', duration: 3000 });
    }
  };

  const reviewer = lastReview?.user ? clean(`${clean(lastReview.user.first_name)} ${clean(lastReview.user.last_name)}`) : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={1}>Resumen de matrícula</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading || !student ? (
            <Flex justify="center" py={10}>
              <Spinner color="purple.500" />
            </Flex>
          ) : (
            <Flex direction="column" gap={4}>
              <Text fontSize="sm" color="gray.600">
                {lastReview
                  ? `Ficha corregida por ${reviewer || 'un usuario'} el ${dayjs(lastReview.createdAt).format('DD/MM/YYYY')}.`
                  : 'La ficha no tiene correcciones registradas por una asesora o admin.'}
              </Text>
              {sections.map((section) => (
                <Box key={section.title}>
                  <Text fontSize="xs" fontWeight="700" letterSpacing="0.08em" color="purple.600" textTransform="uppercase" mb={1}>
                    {section.title}
                  </Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                    {section.empty ? (
                      <Text fontSize="sm" color="gray.500" px={3} py={3}>
                        {section.empty}
                      </Text>
                    ) : (
                      section.rows.map((row) => <CopyRow key={row.label} {...row} />)
                    )}
                  </Box>
                </Box>
              ))}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button colorScheme="purple" leftIcon={<CopyIcon />} onClick={copyAll} isDisabled={!student || loading}>
            Copiar todo
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
